import { useState } from 'react'
import { useCalendarData, type CalendarDay } from '@/common/hooks/useCalendarData'
import * as styles from './CalendarView.css'
import { colors } from '@/core/styles/tokens'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function getLeadingDays(year: number, month: number): number[] {
  // Day of week for the 1st (0=Sun, 1=Mon, ..., 6=Sat)
  const dow = new Date(year, month - 1, 1).getDay()
  // Convert to Mon-based (0=Mon, ..., 6=Sun)
  const monBased = dow === 0 ? 6 : dow - 1
  const prevMonth = month === 1 ? 12 : month - 1
  const prevYear = month === 1 ? year - 1 : year
  const daysInPrev = new Date(prevYear, prevMonth, 0).getDate()
  const leading: number[] = []
  for (let i = monBased - 1; i >= 0; i--) {
    leading.push(daysInPrev - i)
  }
  return leading
}

function getTrailingDays(totalCells: number): number[] {
  const trailing: number[] = []
  const remaining = (7 - (totalCells % 7)) % 7
  for (let i = 1; i <= remaining; i++) {
    trailing.push(i)
  }
  return trailing
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10)
}

interface DayCellProps {
  day: CalendarDay
  payPeriodIndex: number
}

function DayCell({ day, payPeriodIndex }: DayCellProps) {
  const cellClasses = [
    styles.dayCell,
    day.is_weekend ? styles.dayCellWeekend : '',
    payPeriodIndex % 2 === 0 ? styles.dayCellPeriodA : styles.dayCellPeriodB,
  ]
    .filter(Boolean)
    .join(' ')

  const dayNum = parseInt(day.date.split('-')[2], 10)

  return (
    <div className={cellClasses}>
      <span className={isToday(day.date) ? styles.dayNumberToday : styles.dayNumber}>
        {dayNum}
      </span>
      {day.hours > 0 && <span className={styles.hoursTag}>{day.hours}h</span>}
      {day.is_holiday && day.holiday_name && (
        <span className={styles.holidayTag} title={day.holiday_name}>
          {day.holiday_name}
        </span>
      )}
      {day.is_time_off && day.time_off_type && (
        <span className={styles.timeOffTag} title={day.time_off_type}>
          {day.time_off_type}
        </span>
      )}
    </div>
  )
}

export function CalendarView() {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const { data, isLoading, error } = useCalendarData(year, month)

  const goPrev = () => {
    if (month === 1) {
      setYear(year - 1)
      setMonth(12)
    } else {
      setMonth(month - 1)
    }
  }

  const goNext = () => {
    if (month === 12) {
      setYear(year + 1)
      setMonth(1)
    } else {
      setMonth(month + 1)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.monthTitle}>{MONTH_NAMES[month - 1]} {year}</h1>
        </div>
        <div className={styles.loading}>Loading calendar...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.monthTitle}>{MONTH_NAMES[month - 1]} {year}</h1>
        </div>
        <div className={styles.errorBox}>Failed to load calendar: {error.message}</div>
      </div>
    )
  }

  if (!data) return null

  // Build pay period lookup: date string -> period index
  const ppIndex: Record<string, number> = {}
  data.pay_periods.forEach((pp, idx) => {
    const start = new Date(pp.start_date + 'T00:00:00')
    const end = new Date(pp.end_date + 'T00:00:00')
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      ppIndex[d.toISOString().slice(0, 10)] = idx
    }
  })

  const leadingDays = getLeadingDays(year, month)
  const trailingDays = getTrailingDays(leadingDays.length + data.days.length)

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.monthTitle}>{MONTH_NAMES[month - 1]} {year}</h1>
        <div className={styles.navButtons}>
          <button className={styles.navBtn} onClick={goPrev}>
            &larr; Prev
          </button>
          <button className={styles.navBtn} onClick={goNext}>
            Next &rarr;
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {DAY_LABELS.map((d) => (
          <div key={d} className={styles.dayHeader}>
            {d}
          </div>
        ))}

        {leadingDays.map((d, i) => (
          <div key={`lead-${i}`} className={`${styles.dayCell} ${styles.dayCellAdjacent}`}>
            <span className={styles.dayNumber}>{d}</span>
          </div>
        ))}

        {data.days.map((day) => (
          <DayCell key={day.date} day={day} payPeriodIndex={ppIndex[day.date] ?? 0} />
        ))}

        {trailingDays.map((d, i) => (
          <div key={`trail-${i}`} className={`${styles.dayCell} ${styles.dayCellAdjacent}`}>
            <span className={styles.dayNumber}>{d}</span>
          </div>
        ))}
      </div>

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: colors.infoBackground }} />
          Hours logged
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: colors.successBackground }} />
          Holiday
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: colors.infoBackground }} />
          Time off
        </span>
        <span className={styles.legendItem}>
          <span className={styles.legendDot} style={{ backgroundColor: colors.skyLight }} />
          Alternate pay period
        </span>
      </div>
    </div>
  )
}
